import React, { useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import axios from 'axios';
import debounce from 'lodash/debounce';

import {
    ApiEndpoints,
    Defaults,
    SearchParamNames,
    SearchTypeNames,
    SortDirections,
} from '../../../../../api/searchApiConstants';
import { getApiAxiosInstance } from '../../../../../utils/network';
import { showErrorPopup } from '../../../../../actions/notification-actions';
import PopupV2 from '../../../../Popup/PopupV2';
import {
    CppRecruitingStatuses,
    RECRUITING_STATUS_ENDPOINT,
    STATUS_OPTIONS,
} from '../constants';
import styles from '../../collegeProspectTab.scss';

const TESTID_PREFIX = 'modal.college-recruitment-status';
const SCHOOL_SORT_FIELD = 'activeRoster.power6.power6Rating';
const SEARCH_TOP = 10;
const SEARCH_DEBOUNCE_MS = 250;
const MIN_QUERY_LENGTH = 2;
const GENDER_PARAM_MALE = 'M';
const GENDER_PARAM_FEMALE = 'F';

const resolveGenderParam = (gender) => {
    if (typeof gender !== 'string') {
        return null;
    }
    const normalized = gender.trim().toUpperCase();
    if (normalized === 'M' || normalized === 'MALE') {
        return GENDER_PARAM_MALE;
    }
    if (normalized === 'F' || normalized === 'FEMALE') {
        return GENDER_PARAM_FEMALE;
    }
    return null;
};

const buildCollegeSearchParams = (query, genderParam) => {
    const params = new URLSearchParams();
    params.append(SearchParamNames.QUERY, query);
    params.append(SearchParamNames.TOP, SEARCH_TOP);
    params.append(SearchParamNames.SKIP, Defaults.DEFAULT_SKIP);
    params.append(SearchParamNames.SCHOOL_CLUB_SEARCH, Defaults.SCHOOL_CLUB_SEARCH_VALUE);
    params.append(SearchParamNames.SORT, `${SCHOOL_SORT_FIELD}:${SortDirections.DESC}`);
    if (genderParam) {
        params.append(SearchParamNames.GENDER, genderParam);
    }
    return params;
};

const RecruitmentStatusModal = ({
    currentStatusId,
    currentCommittedCollegeProgram,
    onClose,
    onSave,
}) => {
    const userGender = useSelector((state) => state.auth?.user?.gender);
    const genderParam = resolveGenderParam(userGender);
    const dispatch = useDispatch();

    const initialCollegeName = currentStatusId === CppRecruitingStatuses.COMMITTED && currentCommittedCollegeProgram
        ? currentCommittedCollegeProgram.name
        : '';

    const [selectedStatusId, setSelectedStatusId] = useState(currentStatusId);
    const [selectedCollege, setSelectedCollege] = useState(
        currentStatusId === CppRecruitingStatuses.COMMITTED ? currentCommittedCollegeProgram : null,
    );
    const [collegeQuery, setCollegeQuery] = useState(initialCollegeName);
    const [searchResults, setSearchResults] = useState([]);
    const [isSearching, setIsSearching] = useState(false);
    const [hasSearched, setHasSearched] = useState(false);
    const [isSaving, setIsSaving] = useState(false);

    const cancelRef = useRef(null);

    const fetchColleges = useMemo(() => debounce(async (rawQuery, gParam) => {
        if (cancelRef.current) {
            cancelRef.current();
        }
        const source = axios.CancelToken.source();
        cancelRef.current = source.cancel;

        try {
            const response = await getApiAxiosInstance().get(
                `${ApiEndpoints.SEARCH_BASE}/${SearchTypeNames.COLLEGES}`,
                {
                    params: buildCollegeSearchParams(rawQuery, gParam),
                    cancelToken: source.token,
                },
            );
            setSearchResults(response.data?.hits || []);
            setIsSearching(false);
            setHasSearched(true);
        } catch (err) {
            if (axios.isCancel(err)) {
                return;
            }
            setSearchResults([]);
            setIsSearching(false);
            setHasSearched(true);
        }
    }, SEARCH_DEBOUNCE_MS), []);

    const handleStatusChange = (statusId) => {
        setSelectedStatusId(statusId);
        if (statusId !== CppRecruitingStatuses.COMMITTED) {
            setSelectedCollege(null);
            setCollegeQuery('');
            setSearchResults([]);
            setHasSearched(false);
        }
    };

    const handleCollegeQueryChange = (event) => {
        const next = event.target.value;
        setCollegeQuery(next);
        setSelectedCollege(null);
        if (next.trim().length >= MIN_QUERY_LENGTH) {
            setIsSearching(true);
            fetchColleges(next.trim(), genderParam);
        } else {
            fetchColleges.cancel();
            setSearchResults([]);
            setIsSearching(false);
            setHasSearched(false);
        }
    };

    const handleSelectCollege = (hit) => {
        const college = hit.source || hit;
        setSelectedCollege({ id: college.id, name: college.name || college.displayName || '' });
        setCollegeQuery(college.name || college.displayName || '');
        setSearchResults([]);
        setHasSearched(false);
    };

    const handleSave = async () => {
        setIsSaving(true);
        try {
            await getApiAxiosInstance().put(RECRUITING_STATUS_ENDPOINT, {
                statusId: selectedStatusId,
                committedCollegeProgramId: selectedStatusId === CppRecruitingStatuses.COMMITTED
                    ? (selectedCollege?.id ?? null)
                    : null,
            });
            onSave();
        } catch (err) {
            dispatch(showErrorPopup({
                message: 'We couldn\'t save your recruitment status. Please try again.',
                error: err,
            }));
            setIsSaving(false);
        }
    };

    const isSaveDisabled = isSaving ||
        (selectedStatusId === CppRecruitingStatuses.COMMITTED && !selectedCollege);

    const showDropdown = selectedStatusId === CppRecruitingStatuses.COMMITTED &&
        collegeQuery.trim().length >= MIN_QUERY_LENGTH &&
        !selectedCollege;

    return (
        <PopupV2
            onCloseClick={onClose}
            config={{
                size: 'medium',
                customHeader: () => null,
            }}
        >
            <div
                className={styles.recruitingStatusModal}
                data-testid={`${TESTID_PREFIX}.container`}
            >
                <div
                    className={styles.modalTitle}
                    data-testid={`${TESTID_PREFIX}.title`}
                >
                    College Recruitment Status
                </div>
                <p
                    className={styles.modalSubtitle}
                    data-testid={`${TESTID_PREFIX}.subtitle`}
                >
                    Let coaches know your recruitment status.
                </p>

                <div
                    className={styles.statusOptions}
                    data-testid={`${TESTID_PREFIX}.options`}
                >
                    {STATUS_OPTIONS.map((option) => {
                        const isSelected = selectedStatusId === option.id;
                        return (
                            <div
                                key={option.id}
                                className={styles.statusOption}
                                data-testid={`${TESTID_PREFIX}.option-${option.id}`}
                            >
                                <label
                                    className={styles.statusOptionRow}
                                    data-testid={`${TESTID_PREFIX}.option-${option.id}.row`}
                                >
                                    <input
                                        type="radio"
                                        name="recruiting-status"
                                        className={styles.statusRadioInput}
                                        value={option.id}
                                        checked={isSelected}
                                        onChange={() => handleStatusChange(option.id)}
                                        data-testid={`${TESTID_PREFIX}.option-${option.id}.radio`}
                                    />
                                    <span
                                        className={`${styles.statusOptionVisualRadio} ${isSelected ? styles.statusOptionVisualRadioSelected : ''}`}
                                        aria-hidden="true"
                                    />
                                    <div className={styles.statusOptionText}>
                                        <span
                                            className={styles.statusOptionLabel}
                                            data-testid={`${TESTID_PREFIX}.option-${option.id}.label`}
                                        >
                                            {option.label}
                                        </span>
                                        <span
                                            className={styles.statusOptionDescription}
                                            data-testid={`${TESTID_PREFIX}.option-${option.id}.description`}
                                        >
                                            {option.description}
                                        </span>
                                    </div>
                                </label>

                                {isSelected && option.id === CppRecruitingStatuses.COMMITTED && (
                                    <div
                                        className={styles.committedCollegeWrapper}
                                        data-testid={`${TESTID_PREFIX}.committed-college-wrapper`}
                                    >
                                        <div className={styles.collegeSearchWrapper}>
                                            <input
                                                type="text"
                                                className={`form-control ${styles.collegeSearchInput}`}
                                                placeholder="Select a College"
                                                value={collegeQuery}
                                                onChange={handleCollegeQueryChange}
                                                data-testid={`${TESTID_PREFIX}.college-search-input`}
                                            />
                                            {showDropdown && (
                                                <div
                                                    className={styles.collegeSearchDropdown}
                                                    data-testid={`${TESTID_PREFIX}.college-search-dropdown`}
                                                >
                                                    {isSearching && (
                                                        <div
                                                            className={styles.collegeSearchLoading}
                                                            data-testid={`${TESTID_PREFIX}.college-search-loading`}
                                                        >
                                                            <i className="fas fa-circle-notch fa-spin" />
                                                            <span data-testid={`${TESTID_PREFIX}.college-search-loading.text`}>Loading...</span>
                                                        </div>
                                                    )}
                                                    {!isSearching && hasSearched && searchResults.length === 0 && (
                                                        <div
                                                            className={styles.collegeSearchNoMatches}
                                                            data-testid={`${TESTID_PREFIX}.college-search-no-matches`}
                                                        >
                                                            No Matches Found
                                                        </div>
                                                    )}
                                                    {!isSearching && searchResults.length > 0 && (
                                                        <div
                                                            className={styles.collegeSearchResults}
                                                            data-testid={`${TESTID_PREFIX}.college-search-results`}
                                                        >
                                                            {searchResults.map((hit, index) => {
                                                                const source = hit.source || hit;
                                                                const collegeId = source.id || index;
                                                                const displayName = source.name || source.displayName || '';
                                                                const locationDisplay = source.location?.display || '';
                                                                return (
                                                                    <button
                                                                        type="button"
                                                                        key={`college-${collegeId}`}
                                                                        className={styles.collegeSearchResultItem}
                                                                        onClick={() => handleSelectCollege(hit)}
                                                                        data-testid={`${TESTID_PREFIX}.college-search-results.item-${collegeId}`}
                                                                    >
                                                                        <span
                                                                            className={styles.collegeSearchResultName}
                                                                            data-testid={`${TESTID_PREFIX}.college-search-results.item-${collegeId}.name`}
                                                                        >
                                                                            {displayName}
                                                                        </span>
                                                                        {locationDisplay && (
                                                                            <span
                                                                                className={styles.collegeSearchResultLocation}
                                                                                data-testid={`${TESTID_PREFIX}.college-search-results.item-${collegeId}.location`}
                                                                            >
                                                                                {locationDisplay}
                                                                            </span>
                                                                        )}
                                                                    </button>
                                                                );
                                                            })}
                                                        </div>
                                                    )}
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                )}
                            </div>
                        );
                    })}
                </div>

                <div
                    className={styles.modalActions}
                    data-testid={`${TESTID_PREFIX}.actions`}
                >
                    <button
                        type="button"
                        className="btn btn-default btn-sm"
                        onClick={onClose}
                        data-testid={`${TESTID_PREFIX}.cancel-btn`}
                    >
                        CANCEL
                    </button>
                    <button
                        type="button"
                        className="btn btn-primary btn-sm"
                        onClick={handleSave}
                        disabled={isSaveDisabled}
                        data-testid={`${TESTID_PREFIX}.save-btn`}
                    >
                        SAVE
                    </button>
                </div>
            </div>
        </PopupV2>
    );
};

RecruitmentStatusModal.propTypes = {
    currentStatusId: PropTypes.number,
    currentCommittedCollegeProgram: PropTypes.shape({
        id: PropTypes.number,
        name: PropTypes.string,
    }),
    onClose: PropTypes.func.isRequired,
    onSave: PropTypes.func.isRequired,
};

RecruitmentStatusModal.defaultProps = {
    currentStatusId: CppRecruitingStatuses.UNDECIDED,
    currentCommittedCollegeProgram: null,
};

export default RecruitmentStatusModal;
